/**
 * Static, click-free mini preview of a drill_spec — used on the
 * Drills library cards and other listing surfaces. Reads the same
 * fields the drill canvas writes (rolePlayers + arrows + cones +
 * pitchView + customPitch) so the saved drill renders correctly.
 */
import type { DrillSpec, DrillArrow, DrillCone, DrillZone, DrillText, DrillEntity, NormPoint } from '@4cm/core/drills/types';
import {
  pitchViewBox,
  pitchAspect,
  visibleRegion,
  drillNormToPitchSvg,
  type CustomPitch,
  type PitchView,
} from './pitchArea';
import { PITCH_11V11_YOUTH } from './pitchSpecs';
import { roleForId, type DrillRolePlayer } from './playerRoles';
import { entitiesToRolePlayers, resolveArrowAnchors } from './legacyDrillSpec';

interface DrillMiniPreviewProps {
  spec: DrillSpec | null;
  width?: number;
}

interface ExtendedSpec {
  rolePlayers?: DrillRolePlayer[];
  pitchViewExtended?: PitchView;
}

export function DrillMiniPreview({ spec, width = 220 }: DrillMiniPreviewProps): React.JSX.Element | null {
  if (!spec) return null;

  const extras = spec as DrillSpec & ExtendedSpec;
  const view: PitchView = (extras.pitchViewExtended ?? (spec.pitchView as PitchView | undefined) ?? 'full');
  const custom: CustomPitch | null = spec.customPitch
    ? { width: spec.customPitch.width, length: spec.customPitch.length, unit: 'm' }
    : null;
  const pitchSpec = PITCH_11V11_YOUTH; // generic backdrop — drills span teams in the library
  const aspect = pitchAspect(pitchSpec, view, custom);
  const height = width / aspect;
  const region = visibleRegion(pitchSpec, view, custom);

  // Players come from rolePlayers (v2); fall back to projecting legacy
  // entities so a v1 spec also shows its players in the preview.
  const explicitPlayers = extras.rolePlayers ?? [];
  const legacyEntities = (spec.entities ?? []) as DrillEntity[];
  const rolePlayers = explicitPlayers.length > 0
    ? explicitPlayers
    : entitiesToRolePlayers(legacyEntities);
  // Resolve entity-anchored arrow endpoints to the player positions (same as
  // the canvas loader) so the preview draws them instead of collapsing to centre.
  const arrows = resolveArrowAnchors(spec.arrows ?? [], rolePlayers, legacyEntities);
  const cones = spec.cones ?? [];
  const zones = spec.zones ?? [];
  const texts = spec.texts ?? [];

  const STROKE = 'rgba(255, 255, 255, 0.32)';
  const SW = Math.min(0.3, region.width * 0.012);
  const tokenR = Math.max(0.8, region.width * 0.045);
  const arrowSw = Math.max(0.06, region.width * 0.006);
  const headSize = Math.max(0.22, region.length * 0.012);

  const workRect = {
    x: region.cx - region.length / 2,
    y: region.cy - region.width / 2,
    w: region.length,
    h: region.width,
  };

  return (
    <svg
      viewBox={pitchViewBox(pitchSpec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      width={width}
      height={height}
      style={{
        display: 'block',
        background:
          'radial-gradient(70% 70% at 50% 45%, rgba(74, 222, 128, 0.06), transparent 70%),' +
          'linear-gradient(180deg, #0a1f15 0%, #061008 60%, #030504 100%)',
        borderRadius: 8,
      }}
      role="img"
      aria-label="Drill preview"
    >
      {/* Pitch surface. */}
      <rect
        x={workRect.x} y={workRect.y}
        width={workRect.w} height={workRect.h}
        fill="rgba(13, 53, 32, 0.55)"
        stroke={STROKE}
        strokeWidth={SW + 0.1}
      />
      {/* Halfway line + centre circle (only on full view). */}
      {view === 'full' && (
        <>
          <line x1={0} y1={-pitchSpec.width / 2} x2={0} y2={pitchSpec.width / 2} stroke={STROKE} strokeWidth={SW} />
          <circle cx={0} cy={0} r={pitchSpec.centreCircleRadius} fill="none" stroke={STROKE} strokeWidth={SW} />
        </>
      )}

      {/* Zones (drawn behind equipment + players). */}
      {zones.map((z) => (
        <MiniZone key={z.id} zone={z} spec={pitchSpec} view={view} custom={custom} sw={SW} region={region} />
      ))}

      {/* Cones / equipment — small dots to keep the preview readable. */}
      {cones.map((c) => <MiniCone key={c.id} cone={c} spec={pitchSpec} view={view} custom={custom} sw={SW} />)}

      {/* Arrows. */}
      {arrows.map((a) => (
        <MiniArrow key={a.id} arrow={a} spec={pitchSpec} view={view} custom={custom} sw={arrowSw} headSize={headSize} />
      ))}

      {/* Role players (ball + tokens). */}
      {rolePlayers.map((p) => (
        <MiniToken key={p.id} player={p} spec={pitchSpec} view={view} custom={custom} r={tokenR} />
      ))}

      {/* Text labels (on top). */}
      {texts.map((t) => (
        <MiniText key={t.id} text={t} spec={pitchSpec} view={view} custom={custom} />
      ))}
    </svg>
  );
}

// ---- sub-components -------------------------------------------------------

function MiniCone({ cone, spec, view, custom, sw }: {
  cone: DrillCone; spec: typeof PITCH_11V11_YOUTH; view: PitchView; custom: CustomPitch | null; sw: number;
}): React.JSX.Element {
  const { cx, cy } = drillNormToPitchSvg(cone.position, spec, view, custom);
  // Distinguish equipment families with a tiny shape difference.
  const variant = cone.variant ?? 'cone';
  if (variant.startsWith('goal') || variant === 'crossbar') {
    return <rect x={cx - sw * 4} y={cy - sw * 1.5} width={sw * 8} height={sw * 3} fill="#fff" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5} />;
  }
  if (variant === 'mannequin' || variant === 'pole') {
    return <rect x={cx - sw * 0.8} y={cy - sw * 3} width={sw * 1.6} height={sw * 5} fill="#bdc3c7" />;
  }
  // cones / discs / etc.
  return (
    <polygon
      points={`${cx - sw * 1.4},${cy + sw * 1.4} ${cx + sw * 1.4},${cy + sw * 1.4} ${cx},${cy - sw * 1.6}`}
      fill={cone.color ?? '#F39C12'}
      stroke="rgba(0,0,0,0.4)"
      strokeWidth={sw * 0.4}
    />
  );
}

function MiniArrow({ arrow, spec, view, custom, sw, headSize }: {
  arrow: DrillArrow; spec: typeof PITCH_11V11_YOUTH; view: PitchView; custom: CustomPitch | null; sw: number; headSize: number;
}): React.JSX.Element {
  const from = arrow.from.kind === 'point' ? arrow.from.position : ([0.5, 0.5] as NormPoint);
  const to = arrow.to.kind === 'point' ? arrow.to.position : ([0.5, 0.5] as NormPoint);
  const start = drillNormToPitchSvg(from, spec, view, custom);
  const end = drillNormToPitchSvg(to, spec, view, custom);
  const color = arrow.color ?? '#27AE60';
  const dash = arrow.style === 'dashed' ? `${sw * 3.5} ${sw * 2.2}`
             : arrow.style === 'dotted' ? `${sw * 0.5} ${sw * 1.6}`
             : undefined;
  const angle = Math.atan2(end.cy - start.cy, end.cx - start.cx);
  const shorten = headSize * 0.55;
  const endShort = { cx: end.cx - Math.cos(angle) * shorten, cy: end.cy - Math.sin(angle) * shorten };
  const px = -Math.sin(angle) * headSize * 0.55;
  const py = Math.cos(angle) * headSize * 0.55;
  const bx = end.cx - Math.cos(angle) * headSize;
  const by = end.cy - Math.sin(angle) * headSize;
  return (
    <g>
      <path
        d={`M ${start.cx} ${start.cy} L ${endShort.cx} ${endShort.cy}`}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeDasharray={dash}
        strokeLinecap="round"
      />
      <path
        d={`M ${end.cx} ${end.cy} L ${bx + px} ${by + py} L ${bx - px} ${by - py} Z`}
        fill={color}
        stroke={color}
        strokeWidth={sw * 0.4}
      />
    </g>
  );
}

function MiniToken({ player, spec, view, custom, r }: {
  player: DrillRolePlayer; spec: typeof PITCH_11V11_YOUTH; view: PitchView; custom: CustomPitch | null; r: number;
}): React.JSX.Element {
  const role = roleForId(player.roleId);
  const { cx, cy } = drillNormToPitchSvg(player.position, spec, view, custom);
  const isBall = role.id === 'ball';
  const radius = isBall ? r * 0.55 : r;
  if (isBall) {
    return <circle cx={cx} cy={cy} r={radius} fill="#fff" />;
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={radius} fill={role.color} stroke="rgba(0,0,0,0.55)" strokeWidth={radius * 0.13} />
      <text
        x={cx} y={cy + radius * 0.4}
        textAnchor="middle"
        fontSize={radius * 1.05}
        fontWeight={700}
        fill="#fff"
      >
        {player.label ?? role.glyph ?? ''}
      </text>
    </g>
  );
}

function MiniZone({ zone, spec, view, custom, sw, region }: {
  zone: DrillZone; spec: typeof PITCH_11V11_YOUTH; view: PitchView; custom: CustomPitch | null; sw: number; region: { length: number; width: number };
}): React.JSX.Element {
  const fill = zone.fill ?? 'rgba(46, 204, 113, 0.10)';
  const stroke = zone.stroke ?? 'rgba(46, 204, 113, 0.6)';
  const dash = zone.strokeStyle === 'dashed' ? `${sw * 3} ${sw * 2}`
             : zone.strokeStyle === 'dotted' ? `${sw * 0.6} ${sw * 1.6}`
             : undefined;
  if (zone.shape.kind === 'circle') {
    const c = drillNormToPitchSvg(zone.shape.center, spec, view, custom);
    return <circle cx={c.cx} cy={c.cy} r={zone.shape.radiusNorm * region.length} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} />;
  }
  const a = drillNormToPitchSvg(zone.shape.topLeft, spec, view, custom);
  const b = drillNormToPitchSvg(zone.shape.bottomRight, spec, view, custom);
  return (
    <rect
      x={Math.min(a.cx, b.cx)} y={Math.min(a.cy, b.cy)}
      width={Math.abs(b.cx - a.cx)} height={Math.abs(b.cy - a.cy)}
      fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} rx={sw}
    />
  );
}

function MiniText({ text, spec, view, custom }: {
  text: DrillText; spec: typeof PITCH_11V11_YOUTH; view: PitchView; custom: CustomPitch | null;
}): React.JSX.Element {
  const { cx, cy } = drillNormToPitchSvg(text.position, spec, view, custom);
  const size = Math.max(0.8, (text.fontSize ?? 1.4) * 0.9); // yards → ~metres
  const color = text.color ?? 'rgba(255,255,255,0.92)';
  const lines = text.content.split('\n');
  const rot = text.rotation ?? 0;
  return (
    <text
      x={cx} y={cy}
      textAnchor="middle"
      fontSize={size}
      fontWeight={700}
      fill={color}
      transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={cx} dy={i === 0 ? 0 : size * 1.15}>{ln}</tspan>
      ))}
    </text>
  );
}
